import type { BridgeNtState, BridgeSide } from '../games/bridge-nt';

export interface BridgeScoreData {
  side: BridgeSide;
  sideLabel: 'NS' | 'EW';
  actual: number | null;
  dd: number | null;
  delta: number | null;
  relation: 'above' | 'equal' | 'below' | 'unknown';
  ddStatus: '配牌中' | '未採点' | '不明';
}

const trickCount = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 13 ? value : null;

/** Rust BridgeOutcome is NS-based. Both counts complement to 13 for EW;
 * therefore (13 - actualNS) - (13 - ddNS) = -ns_score.
 * The DD basis is the original deal, opening leader and Notrump, not the
 * currently displayed replay position. Never manufacture a missing DD value.
 */
export function bridgeScoreComparison(state: BridgeNtState, yourSide: BridgeSide | null): BridgeScoreData {
  const side = yourSide ?? 'First';
  const nsActual = trickCount(state.outcome?.ns_tricks);
  const actual = nsActual !== null
    ? side === 'First' ? nsActual : 13 - nsActual
    : trickCount(state.tricks_won?.[side === 'First' ? 0 : 1]);
  const nsDd = trickCount(state.outcome?.dd_ns_tricks);
  const dd = nsDd === null ? null : side === 'First' ? nsDd : 13 - nsDd;
  const delta = actual === null || dd === null ? null : actual - dd;
  return {
    side,
    sideLabel: side === 'First' ? 'NS' : 'EW',
    actual,
    dd,
    delta,
    relation: delta === null ? 'unknown' : delta > 0 ? 'above' : delta < 0 ? 'below' : 'equal',
    ddStatus: state.phase === 'awaiting_hands' ? '配牌中' : state.phase === 'complete' ? '不明' : '未採点',
  };
}

export function bridgeScoreDelta(delta: number | null): string {
  return delta === null ? '—' : delta > 0 ? `+${delta}` : delta < 0 ? `−${-delta}` : '±0';
}
